import * as THREE from "three";
import * as WebIFC from "web-ifc";
import { type IfcRevisionEntry } from "../state";
import { ifcLoader, ensureIfcLoaderReady } from "../viewer/scene";
import { getActiveFragmentsModel } from "../fragments/engine";
import { unwrapIfcValue } from "./props";
import { maybeUnzipIfczip } from "./ifczip";

/**
 * Поля IfcRoot, которые мы включаем в fingerprint. Этого набора достаточно,
 * чтобы поймать переименование, смену тега, изменение описания/типа.
 * Геометрию мы не хэшируем — это задача отдельного этапа (Stage 2+).
 */
const FINGERPRINT_PROPS: readonly string[] = [
  "Name",
  "Tag",
  "ObjectType",
  "Description",
  "PredefinedType",
  "LongName", // IfcSite/IfcBuilding/IfcSpace
];

function buildFingerprint(node: Record<string, unknown>, type: string): string {
  const parts: string[] = [type];
  for (const key of FINGERPRINT_PROPS) {
    parts.push(unwrapIfcValue(node[key]));
  }
  return parts.join("|");
}

function nodeExpressId(node: any): number {
  const id = Number(node?.localId ?? node?.expressID);
  return Number.isFinite(id) ? id : -1;
}

function nodeType(node: any): string {
  return String(node?.category ?? node?.type ?? "");
}

/**
 * Обходит дерево spatial structure и собирает карту GlobalId → snapshot.
 */
export function snapshotFromSpatialTree(root: any): Map<string, IfcRevisionEntry> {
  const map = new Map<string, IfcRevisionEntry>();
  if (!root || typeof root !== "object") {
    return map;
  }
  const stack: any[] = [root];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node || typeof node !== "object") {
      continue;
    }
    const globalId = unwrapIfcValue((node as any).GlobalId);
    if (globalId) {
      const type = nodeType(node);
      const expressId = nodeExpressId(node);
      const fingerprint = buildFingerprint(node as Record<string, unknown>, type);
      if (!map.has(globalId)) {
        map.set(globalId, {
          expressId: expressId > 0 ? expressId : -1,
          type,
          fingerprint,
        });
      }
    }
    const children = (node as any).children;
    if (Array.isArray(children)) {
      for (const child of children) {
        stack.push(child);
      }
    }
  }
  return map;
}

async function snapshotFromFragmentsModel(
  modelId: string,
): Promise<Map<string, IfcRevisionEntry>> {
  const model = getActiveFragmentsModel(modelId);
  if (!model) {
    return new Map();
  }
  const localIds = await model.getLocalIds();
  const guids = await model.getGuidsByLocalIds(localIds);
  // Оставляем только корневые сущности (у которых есть GlobalId). getLocalIds()
  // возвращает ВСЕ сущности модели, включая геометрические примитивы; вызывать
  // getItemsData для них — пустая трата времени (на больших моделях это и делало
  // построение снимка текущей ревизии неприемлемо медленным).
  const rooted: { localId: number; guid: string }[] = [];
  for (let i = 0; i < localIds.length; i++) {
    const guid = guids[i];
    if (guid) {
      rooted.push({ localId: localIds[i], guid: String(guid) });
    }
  }
  const map = new Map<string, IfcRevisionEntry>();
  const chunkSize = 500;
  for (let offset = 0; offset < rooted.length; offset += chunkSize) {
    const slice = rooted.slice(offset, offset + chunkSize);
    const sliceIds = slice.map((r) => r.localId);
    const rows = await model.getItemsData(sliceIds, { attributesDefault: true });
    for (let i = 0; i < slice.length; i++) {
      const localId = slice[i].localId;
      const guid = slice[i].guid || unwrapIfcValue(rows[i]?.GlobalId);
      if (!guid) {
        continue;
      }
      const props = rows[i] || {};
      const categoryRaw = (props as any)._category;
      const type = String(
        unwrapIfcValue(categoryRaw) ||
          unwrapIfcValue((props as any).type) ||
          "",
      ).toUpperCase();
      const plain: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(props)) {
        if (value && typeof value === "object" && "value" in (value as object)) {
          plain[key] = (value as { value: unknown }).value;
        } else {
          plain[key] = value;
        }
      }
      map.set(String(guid), {
        expressId: localId,
        type,
        fingerprint: buildFingerprint(plain, type),
      });
    }
  }
  return map;
}

/**
 * Строит snapshot ревизии для УЖЕ загруженной модели (текущей вкладки).
 */
export async function buildRevisionSnapshotFromModel(
  modelID: string | number,
): Promise<Map<string, IfcRevisionEntry>> {
  await ensureIfcLoaderReady();
  const id = String(modelID);
  const fragModel = getActiveFragmentsModel(id);
  if (fragModel) {
    return snapshotFromFragmentsModel(id);
  }

  const tree = await ifcLoader.ifcManager.getSpatialStructure(modelID, true);
  const map = snapshotFromSpatialTree(tree);

  const rootExpressId = nodeExpressId(tree);
  if (rootExpressId > 0) {
    try {
      const rootProps = await ifcLoader.ifcManager.getItemProperties(
        modelID,
        rootExpressId,
        false,
      );
      const globalId = unwrapIfcValue(rootProps?.GlobalId);
      if (globalId && !map.has(globalId)) {
        const type = nodeType(tree) || String(rootProps?.type || "IFCPROJECT");
        map.set(globalId, {
          expressId: rootExpressId,
          type,
          fingerprint: buildFingerprint(rootProps as Record<string, unknown>, type),
        });
      }
    } catch (_) {
      /* не критично */
    }
  }
  return map;
}

// --- Лёгкий парсинг базового IFC через web-ifc (только атрибуты, без геометрии) ---
// Ранее базовый файл грузился через полную конвертацию в Fragments
// (loadIfcAsFragments → мешинг геометрии) ради чтения одних только GlobalId.
// На больших файлах (десятки–сотни МБ) это «вешало» загрузку базовой ревизии.
// web-ifc парсит STEP без построения геометрии — на порядок быстрее и легче.

let webIfcApi: WebIFC.IfcAPI | null = null;
let webIfcInitPromise: Promise<WebIFC.IfcAPI> | null = null;

function webIfcWasmDir(): string {
  return new URL(`${import.meta.env.BASE_URL}ifc-wasm/`, window.location.origin).toString();
}

async function getWebIfcApi(): Promise<WebIFC.IfcAPI> {
  if (webIfcApi) {
    return webIfcApi;
  }
  if (!webIfcInitPromise) {
    webIfcInitPromise = (async () => {
      const api = new WebIFC.IfcAPI();
      api.SetWasmPath(webIfcWasmDir(), true);
      await api.Init();
      webIfcApi = api;
      return api;
    })().catch((error) => {
      webIfcInitPromise = null;
      throw error;
    });
  }
  return webIfcInitPromise;
}

async function snapshotViaWebIfc(bytes: Uint8Array): Promise<Map<string, IfcRevisionEntry>> {
  const api = await getWebIfcApi();
  const map = new Map<string, IfcRevisionEntry>();
  const modelID = api.OpenModel(bytes);
  try {
    // Перечисляем ТУ ЖЕ популяцию, что и Fragments-снимок текущей модели:
    // физические элементы (IfcElement) + пространственная структура
    // (площадка/здание/этаж/помещение) + проект. Если брать весь IfcRoot
    // (includeInherited), сюда попадут связи IfcRel* и наборы свойств
    // IfcPropertySet — их у Fragments-снимка нет, и сравнение даст массу
    // ложных «removed».
    const idSet = new Set<number>();
    const WANTED_TYPES = ["IFCELEMENT", "IFCSPATIALSTRUCTUREELEMENT", "IFCPROJECT"];
    for (const typeName of WANTED_TYPES) {
      try {
        const code = api.GetTypeCodeFromName(typeName);
        const vec = api.GetLineIDsWithType(modelID, code, true);
        const n = vec.size();
        for (let i = 0; i < n; i++) {
          idSet.add(vec.get(i));
        }
      } catch (_) {
        /* тип не найден в схеме — пропускаем */
      }
    }
    // Резерв: если по типам ничего не нашли (нестандартная схема) — все линии.
    let ids: number[];
    if (idSet.size > 0) {
      ids = [...idSet];
    } else {
      const all = api.GetAllLines(modelID);
      const n = all.size();
      ids = new Array(n);
      for (let i = 0; i < n; i++) {
        ids[i] = all.get(i);
      }
    }
    const size = ids.length;
    for (let i = 0; i < size; i++) {
      const expressId = ids[i];
      let line: any;
      try {
        line = api.GetLine(modelID, expressId);
      } catch (_) {
        continue;
      }
      const guid = unwrapIfcValue(line?.GlobalId);
      if (!guid) {
        continue;
      }
      let typeName = "";
      try {
        typeName = api.GetNameFromTypeCode(line.type) || "";
      } catch (_) {
        typeName = "";
      }
      const type = String(typeName).toUpperCase();
      if (!map.has(guid)) {
        map.set(guid, { expressId, type, fingerprint: buildFingerprint(line, type) });
      }
    }
  } finally {
    try {
      api.CloseModel(modelID);
    } catch (_) {
      /* ignore */
    }
  }
  return map;
}

/**
 * Строит GUID-snapshot базового IFC-файла без построения геометрии (web-ifc).
 * Fingerprint считается по тем же полям, что и для текущей модели, поэтому
 * сравнение current↔baseline корректно. Поддерживает IFCZIP (через распаковку).
 */
export async function buildRevisionSnapshotFromFile(
  file: File,
): Promise<Map<string, IfcRevisionEntry>> {
  const inner = await maybeUnzipIfczip(file);
  const bytes = new Uint8Array(await inner.arrayBuffer());
  return snapshotViaWebIfc(bytes);
}

/**
 * Строит THREE-геометрию указанных элементов базового IFC-файла (по express ID)
 * для наложения «призраком» поверх текущей модели — используется, чтобы
 * показать удалённые элементы (есть только в базовой ревизии) красным.
 *
 * Важно: открываем web-ifc с COORDINATE_TO_ORIGIN:true — ровно как это делает
 * Fragments-загрузчик для текущей модели. Иначе геометрия базовой ревизии
 * окажется в мировых координатах и «уедет» относительно текущей модели
 * (особенно для геопривязанных проектов).
 */
export async function buildRemovedGeometry(
  file: File,
  expressIds: number[],
  material: THREE.Material,
): Promise<THREE.Object3D | null> {
  if (expressIds.length === 0) {
    return null;
  }
  const api = await getWebIfcApi();
  const inner = await maybeUnzipIfczip(file);
  const bytes = new Uint8Array(await inner.arrayBuffer());
  const modelID = api.OpenModel(bytes, { COORDINATE_TO_ORIGIN: true });

  const positions: number[] = [];
  const indices: number[] = [];
  let indexOffset = 0;
  const v = new THREE.Vector3();
  const m = new THREE.Matrix4();

  try {
    api.StreamMeshes(modelID, expressIds, (mesh) => {
      const geoms = mesh.geometries;
      const count = geoms.size();
      for (let i = 0; i < count; i++) {
        const placed = geoms.get(i);
        let geom: WebIFC.IfcGeometry | null = null;
        try {
          geom = api.GetGeometry(modelID, placed.geometryExpressID);
          const verts = api.GetVertexArray(geom.GetVertexData(), geom.GetVertexDataSize());
          const idx = api.GetIndexArray(geom.GetIndexData(), geom.GetIndexDataSize());
          m.fromArray(placed.flatTransformation as unknown as number[]);
          // web-ifc отдаёт по 6 float на вершину: позиция (xyz) + нормаль (xyz).
          const vertexCount = verts.length / 6;
          for (let k = 0; k < vertexCount; k++) {
            v.set(verts[k * 6], verts[k * 6 + 1], verts[k * 6 + 2]).applyMatrix4(m);
            positions.push(v.x, v.y, v.z);
          }
          for (let k = 0; k < idx.length; k++) {
            indices.push(idx[k] + indexOffset);
          }
          indexOffset += vertexCount;
        } finally {
          if (geom && typeof geom.delete === "function") {
            geom.delete();
          }
        }
      }
      if (typeof (mesh as { delete?: () => void }).delete === "function") {
        (mesh as { delete: () => void }).delete();
      }
    });
  } finally {
    try {
      api.CloseModel(modelID);
    } catch (_) {
      /* ignore */
    }
  }

  if (positions.length === 0) {
    return null;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  const meshObject = new THREE.Mesh(geometry, material);
  meshObject.name = "compare-removed-ghost";
  meshObject.renderOrder = 2;
  return meshObject;
}
